import { Button, FormControl, NativeSelect, OutlinedInput, Typography } from "@mui/material";
import React, { useState } from "react";
import { UserResponse } from "../../../api/mosaic/interface";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";
import { WEBSITE_REDIRECT_OPTIONS } from "./websiteRedirectOptions";

interface IProps {
  user: UserResponse;
  isLoading: boolean;
  handleClose: () => void;
  handleReactivate: (websiteRedirect: string) => void;
}

/**
 * Reactivating a user who never activated sends them a new activation email, so the
 * admin picks which site its link opens. Users with a password skip this and only
 * confirm: they sign in as before.
 */
const ReactivateUserModal: React.FC<IProps> = ({ user, isLoading, handleClose, handleReactivate }) => {
  // Default to app.surgohealth.com: takeda.surgohealth.com is being retired
  const [websiteRedirect, setWebsiteRedirect] = useState(process.env.REACT_APP_GLOBAL_WEBSITE_URL || "");

  return (
    <ModalWrapper
      open
      onClose={handleClose}
      title="Reactivate User"
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} sx={modalCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleReactivate(websiteRedirect)}
            disabled={!websiteRedirect || isLoading}
            sx={modalSubmitButtonSx}
          >
            {isLoading ? "Reactivating..." : "Reactivate"}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ color: "#1E1E1E", mb: 3 }}>
        {user.email} never activated their account, so reactivating it sends a new activation email.
        Choose the site the activation link opens.
      </Typography>
      <FormControl fullWidth>
        <NativeSelect
          name="websiteRedirect"
          value={websiteRedirect}
          input={<OutlinedInput sx={{ borderRadius: "8px" }} />}
          onChange={(e) => setWebsiteRedirect(e.target.value)}
        >
          <option value="">Select a website redirect *</option>
          {/* An unset env var would otherwise submit the label as the URL. */}
          {WEBSITE_REDIRECT_OPTIONS.filter((option) => option.value).map((option) => (
            <option value={option.value} key={option.label}>{option.label}</option>
          ))}
        </NativeSelect>
      </FormControl>
    </ModalWrapper>
  );
};

export default ReactivateUserModal;
